import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, UtensilsCrossed, Image as ImageIcon, Info, BookOpen,
  ChefHat, Heart, Calendar, Users, Home, LogOut, Menu as MenuIcon, X,
  FileText, Navigation as NavIcon, Layout as LayoutIcon, Mail, Send, Star,
  BarChart3,
} from 'lucide-react';

import Overview from './managers/Overview';
import HeroManager from './managers/HeroManager';
import MenuManager from './managers/MenuManager';
import MenuHighlightsManager from './managers/MenuHighlightsManager';
import GalleryManager from './managers/GalleryManager';
import RestaurantInfoManager from './managers/RestaurantInfoManager';
import StoryManager from './managers/StoryManager';
import ChefsTableManager from './managers/ChefsTableManager';
import CommunityManager from './managers/CommunityManager';
import PrivateEventsManager from './managers/PrivateEventsManager';
import UsersManager from './managers/UsersManager';
import PageManager from './managers/PageManager';
import NavManager from './managers/NavManager';
import FooterManager from './managers/FooterManager';
import NewsletterCopyManager from './managers/NewsletterCopyManager';
import SubscribersManager from './managers/SubscribersManager';
import EventInquiriesManager from './managers/EventInquiriesManager';
import PressManager from './managers/PressManager';
import SocialPostsManager from './managers/SocialPostsManager';
import VisitInfoManager from './managers/VisitInfoManager';
import MarketingManager from './managers/MarketingManager';
import ReportsManager from './managers/ReportsManager';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, group: 'Dashboard' },
  { id: 'reports', label: 'Reports', icon: BarChart3, group: 'Dashboard' },

  { id: 'hero', label: 'Hero Slideshow', icon: Home, group: 'Homepage' },
  { id: 'press', label: 'Press / Featured In', icon: Star, group: 'Homepage' },
  { id: 'menu-highlights', label: 'Menu Highlights', icon: Star, group: 'Homepage' },
  { id: 'gallery', label: 'Gallery', icon: ImageIcon, group: 'Homepage' },
  { id: 'social-posts', label: 'Instagram Feed', icon: ImageIcon, group: 'Homepage' },
  { id: 'visit-info', label: 'Visit Us (Good to Know)', icon: Info, group: 'Homepage' },
  { id: 'story', label: 'Story (Section)', icon: BookOpen, group: 'Homepage' },
  { id: 'chefs-table', label: "Chef's Table (Section)", icon: ChefHat, group: 'Homepage' },

  { id: 'pages', label: 'Page Content', icon: FileText, group: 'Pages' },
  { id: 'menu-items', label: 'Menu Items', icon: UtensilsCrossed, group: 'Pages' },

  { id: 'community', label: 'Community Initiatives', icon: Heart, group: 'Data' },
  { id: 'private-events', label: 'Event Packages', icon: Calendar, group: 'Data' },
  { id: 'restaurant-info', label: 'Restaurant Info', icon: Info, group: 'Data' },

  { id: 'nav', label: 'Navigation', icon: NavIcon, group: 'Site Chrome' },
  { id: 'footer', label: 'Footer', icon: LayoutIcon, group: 'Site Chrome' },
  { id: 'newsletter-copy', label: 'Newsletter Copy', icon: Mail, group: 'Site Chrome' },
  { id: 'subscribers', label: 'Subscribers', icon: Send, group: 'Site Chrome' },
  { id: 'event-inquiries', label: 'Event Inquiries', icon: Calendar, group: 'Site Chrome' },

  { id: 'users', label: 'Admin Users', icon: Users, group: 'System' },
  { id: 'marketing', label: 'Marketing & Analytics', icon: BarChart3, group: 'System' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [active, setActive] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const stored = localStorage.getItem('adminUser');
    if (!token || !stored) {
      navigate('/admin');
      return;
    }
    setUser(JSON.parse(stored));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  if (!user) return null;

  const renderContent = () => {
    switch (active) {
      case 'overview': return <Overview />;
      case 'reports': return <ReportsManager />;
      case 'hero': return <HeroManager onMessage={showMessage} />;
      case 'menu-items': return <MenuManager onMessage={showMessage} />;
      case 'menu-highlights': return <MenuHighlightsManager onMessage={showMessage} />;
      case 'gallery': return <GalleryManager onMessage={showMessage} />;
      case 'restaurant-info': return <RestaurantInfoManager onMessage={showMessage} />;
      case 'story': return <StoryManager onMessage={showMessage} />;
      case 'chefs-table': return <ChefsTableManager onMessage={showMessage} />;
      case 'community': return <CommunityManager onMessage={showMessage} />;
      case 'private-events': return <PrivateEventsManager onMessage={showMessage} />;
      case 'users': return <UsersManager onMessage={showMessage} currentUserId={user.id} />;
      case 'pages': return <PageManager onMessage={showMessage} />;
      case 'nav': return <NavManager onMessage={showMessage} />;
      case 'footer': return <FooterManager onMessage={showMessage} />;
      case 'newsletter-copy': return <NewsletterCopyManager onMessage={showMessage} />;
      case 'subscribers': return <SubscribersManager onMessage={showMessage} />;
      case 'event-inquiries': return <EventInquiriesManager onMessage={showMessage} />;
      case 'press': return <PressManager onMessage={showMessage} />;
      case 'social-posts': return <SocialPostsManager onMessage={showMessage} />;
      case 'visit-info': return <VisitInfoManager onMessage={showMessage} />;
      case 'marketing': return <MarketingManager onMessage={showMessage} />;
      default: return <p>Coming soon</p>;
    }
  };

  // Group nav items
  const groups = NAV_ITEMS.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  const activeLabel = NAV_ITEMS.find((n) => n.id === active)?.label || 'Dashboard';

  return (
    <div className="min-h-screen bg-gray-100">
      {message.text && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${message.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`} data-testid="admin-toast">
          {message.text}
        </div>
      )}

      <aside className={`fixed left-0 top-0 h-full bg-[#1a1a1a] text-white transition-all duration-300 z-40 ${sidebarOpen ? 'w-64' : 'w-20'} overflow-y-auto`}>
        <div className="p-4 flex items-center justify-between border-b border-gray-700 sticky top-0 bg-[#1a1a1a] z-10">
          {sidebarOpen && <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>BrickLane</h1>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-700 rounded">
            {sidebarOpen ? <X size={20} /> : <MenuIcon size={20} />}
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} className="mb-3">
              {sidebarOpen && <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider px-3 py-2">{group}</p>}
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActive(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition text-sm ${active === item.id ? 'bg-[#C9A86C] text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <item.icon size={18} />
                  {sidebarOpen && <span>{item.label}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-700 sticky bottom-0 bg-[#1a1a1a]">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition" data-testid="admin-logout-btn">
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
          {sidebarOpen && (
            <Link to="/" className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition mt-1">
              <Home size={18} />
              <span>View Website</span>
            </Link>
          )}
        </div>
      </aside>

      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <header className="bg-white shadow-sm px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>{activeLabel}</h1>
            <span className="text-gray-600 text-sm">Welcome, {user?.name}</span>
          </div>
        </header>
        <div className="p-6">{renderContent()}</div>
      </main>
    </div>
  );
};

export default AdminDashboard;
